import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Users } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { Contact, ContactFormInput, ContactUpdateInput } from '../../../customers/api';
import {
  CONTACT_ROLE_LABELS,
  createContact,
  deleteContact,
  listContacts,
  listSites,
  updateContact,
} from '../../../customers/api';
import {
  CORPORATE_SITE_VALUE,
  mapContactActionError,
  mapContactFormError,
  shouldRefetchContactsOnError,
  shouldRefetchSitesOnError,
  type ContactFormFields,
  type ContactFormValues,
} from '../../../customers/contact-form-schema';
import { ContactForm } from './contact-form';

export interface CustomerContactsTabProps {
  customerId: string;
}

type ModalState = { mode: 'create' } | { mode: 'edit'; contact: Contact } | null;

/**
 * `customer-contacts/spec.md` → "Pestaña Contactos en back-office". Order
 * is the API's own (`design.md` §6.2, decision 4: corporate first, then
 * `fullName`/`id`); this tab does not re-sort. Sites are loaded via the
 * existing sites query to resolve site names and populate the site select.
 */
export function CustomerContactsTab({ customerId }: CustomerContactsTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const canManage = hasPermission(currentUser.data, 'customer.update');
  const [modal, setModal] = useState<ModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const contactsQuery = useQuery({
    queryKey: ['customer-contacts', customerId],
    queryFn: () => listContacts(customerId),
  });

  const sitesQuery = useQuery({
    queryKey: ['customer-sites', customerId],
    queryFn: () => listSites(customerId),
  });

  async function invalidateContacts(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['customer-contacts', customerId] });
  }

  async function invalidateSites(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['customer-sites', customerId] });
  }

  const createMutation = useMutation({
    mutationFn: (input: ContactFormInput) => createContact(customerId, input),
    onSuccess: async () => {
      await invalidateContacts();
      setModal(null);
    },
    onError: (error) => {
      if (shouldRefetchSitesOnError(error)) {
        void invalidateSites();
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ contactId, input }: { contactId: string; input: ContactUpdateInput }) =>
      updateContact(customerId, contactId, input),
    onSuccess: async () => {
      await invalidateContacts();
      setModal(null);
    },
    onError: (error) => {
      if (shouldRefetchSitesOnError(error)) {
        void invalidateSites();
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => deleteContact(customerId, contactId),
    onSuccess: () => invalidateContacts(),
    onError: (error) => handleActionError(error),
  });

  function handleActionError(error: unknown): void {
    setActionError(mapContactActionError(error));

    if (shouldRefetchContactsOnError(error)) {
      void invalidateContacts();
    }
  }

  if (contactsQuery.isPending || sitesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (contactsQuery.isError || sitesQuery.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar los contactos">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void contactsQuery.refetch();
              void sitesQuery.refetch();
            }}
          >
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const contacts = contactsQuery.data;
  const sites = sitesQuery.data;
  const siteNameById = new Map(sites.map((site) => [site.id, site.name] as const));

  const createFormError = mapContactFormError(createMutation.error);
  const updateFormError = mapContactFormError(updateMutation.error);
  const formErrors = modal?.mode === 'create' ? createFormError : updateFormError;

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Contactos</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nuevo contacto
          </Button>
        ) : null}
      </div>

      {contacts.length === 0 ? (
        <EmptyState icon={Users} title="Esta empresa todavía no tiene contactos cargados." />
      ) : (
        <Table>
          <caption className="sr-only">Contactos de la empresa</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Rol</TableHeader>
              <TableHeader>Sede</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Teléfono</TableHeader>
              <TableHeader>Notificaciones</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {contacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                canManage={canManage}
                siteName={
                  contact.siteId === null
                    ? 'Corporativo'
                    : (siteNameById.get(contact.siteId) ?? '—')
                }
                deleting={deleteMutation.isPending && deleteMutation.variables === contact.id}
                onEdit={() => setModal({ mode: 'edit', contact })}
                onDelete={() => {
                  if (window.confirm(`¿Eliminar el contacto "${contact.fullName}"?`)) {
                    deleteMutation.mutate(contact.id);
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {modal !== null ? (
        <ContactForm
          title={modal.mode === 'create' ? 'Nuevo contacto' : 'Editar contacto'}
          submitLabel={modal.mode === 'create' ? 'Crear contacto' : 'Guardar cambios'}
          submitting={createMutation.isPending || updateMutation.isPending}
          sites={sites}
          {...(modal.mode === 'edit' ? { defaultValues: toFormValues(modal.contact) } : {})}
          {...(formErrors.formError === undefined ? {} : { formError: formErrors.formError })}
          {...(formErrors.fieldError === undefined ? {} : { fieldError: formErrors.fieldError })}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation.mutateAsync(toCreateInput(values)).catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({
                contactId: modal.contact.id,
                input: diffChanges(modal.contact, values),
              })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

interface ContactRowProps {
  contact: Contact;
  canManage: boolean;
  siteName: string;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function ContactRow({
  contact,
  canManage,
  siteName,
  deleting,
  onEdit,
  onDelete,
}: ContactRowProps): JSX.Element {
  return (
    <TableRow>
      <TableCell className="align-top">{contact.fullName}</TableCell>
      <TableCell className="align-top">{CONTACT_ROLE_LABELS[contact.role]}</TableCell>
      <TableCell className="align-top">{siteName}</TableCell>
      <TableCell className="align-top">{contact.email ?? '—'}</TableCell>
      <TableCell className="align-top">{contact.phone ?? '—'}</TableCell>
      <TableCell className="align-top">
        <div className="flex flex-col gap-1">
          {contact.receivesNotifications ? (
            <Badge variant="primary">Recibe notificaciones</Badge>
          ) : null}
          {contact.hasPortalUser ? <Badge variant="neutral">Usuario del portal</Badge> : null}
        </div>
      </TableCell>
      <TableCell className="align-top">
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Editar
            </Button>
            <Button variant="destructive" size="sm" loading={deleting} onClick={onDelete}>
              Eliminar
            </Button>
          </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function toFormValues(contact: Contact): ContactFormFields {
  return {
    fullName: contact.fullName,
    role: contact.role,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    receivesNotifications: contact.receivesNotifications,
    siteId: contact.siteId ?? CORPORATE_SITE_VALUE,
  };
}

function toCreateInput(values: ContactFormValues): ContactFormInput {
  return {
    fullName: values.fullName,
    role: values.role,
    receivesNotifications: values.receivesNotifications,
    siteId: values.siteId === CORPORATE_SITE_VALUE ? null : values.siteId,
    ...(values.email === '' ? {} : { email: values.email }),
    ...(values.phone === '' ? {} : { phone: values.phone }),
  };
}

/** A PATCH sends only the fields that actually changed, same convention as `diffChanges` in `CustomerSitesTab`. */
function diffChanges(contact: Contact, values: ContactFormValues): ContactUpdateInput {
  const changes: ContactUpdateInput = {};

  if (values.fullName !== contact.fullName) changes.fullName = values.fullName;
  if (values.role !== contact.role) changes.role = values.role;

  const newEmail = values.email === '' ? null : values.email;
  if (newEmail !== contact.email) changes.email = values.email;

  const newPhone = values.phone === '' ? null : values.phone;
  if (newPhone !== contact.phone) changes.phone = values.phone;

  if (values.receivesNotifications !== contact.receivesNotifications) {
    changes.receivesNotifications = values.receivesNotifications;
  }

  const newSiteId = values.siteId === CORPORATE_SITE_VALUE ? null : values.siteId;
  if (newSiteId !== contact.siteId) changes.siteId = newSiteId;

  return changes;
}
